import { assertEvent, assign } from 'xstate5';

import { getElementConfig, getIntentConfig, gravyOrderReturnURL } from '@order/integrations-stripe';

import { prepareLedger } from '../utils';
import { OrderingPaymentActions } from './ordering-machine.actions.payment';
import { OrderingPostOrderActions } from './ordering-machine.actions.post';
import { OrderingRewardsActions } from './ordering-machine.actions.rewards';
import { OrderingTimeActions } from './ordering-machine.actions.time';
import {
  type ConvertToGroupOrderErrorParams,
  type GroupOrderLockReasonParams,
  type LeaveGroupOrderErrorParams,
  type LockGroupOrderErrorParams,
  type OrderingMachineAction,
} from './ordering-machine.actions.types';
import {
  FetchPaymentBreakdownResponse,
  type FetchInitialDataResponse,
} from './ordering-machine.actors';
import { initialContext } from './ordering-machine.context';
import { orderingMachineLog } from './ordering-machine.logger';
import { type OrderingCart } from './ordering-machine.types';
import { getNewTip } from './ordering-machine.utils';

// ─────────────────────────────────────────────────────────────────────────────

const resetContext: OrderingMachineAction = assign(({ context }) => {
  return {
    ...initialContext,
    orderSubmissionDelay: context.orderSubmissionDelay,
    wantedTimesPollingInterval: context.wantedTimesPollingInterval,
    bagCartPollingInterval: context.bagCartPollingInterval,
    stripeConfirmHandlerRef: context.stripeConfirmHandlerRef,
    confirmStripe: context.confirmStripe,
  };
});

const onFetchInitialDataDone: OrderingMachineAction<FetchInitialDataResponse> = assign(
  ({ context }, params) => {
    const { cart, selectedTip, availableCredit } = params;
    const { stripeConfirmHandlerRef } = context;

    const { total } = prepareLedger({
      ledger: cart.ledger,
      tip: selectedTip,
      availableCredit,
      shouldUseCredit: true,
      amountEligibleForFlex: 0,
      shouldUseFlex: true,
    });

    const amount = total <= 0 ? 100 : total;

    return {
      isWantedTimeAsap: true,
      orderId: cart.orderId,
      cart,
      paymentMethods: params.paymentMethods,
      defaultPaymentMethodId: params.defaultPaymentMethodId,
      flexPaymentMethod: params.flexPaymentMethod,
      hasDefaultPaymentMethodCard: params.hasDefaultPaymentMethodCard,
      selectedTip,
      availableTips: params.availableTips,
      availablePoints: params.availablePoints,
      availableCredit,
      giftCardBalance: params.giftCardBalance,
      customer: {
        customerId: params.customerId,
        firstName: params.firstName,
        lastName: params.lastName,
        email: params.email,
        phoneNumber: params.phoneNumber,
        isNewCustomer: params.isNewCustomer,
        isOptedOutFromLoyalty: params.isOptedOutFromLoyalty,
      },
      wantedTime: cart.wantedTimes[0],
      dropoffLocation: cart.dropoffLocations[0]?.value,
      appliedRewardId: cart.appliedRewardId,
      stripeCustomerId: params.stripeCustomerId,
      stripeCustomerKey: params.stripeCustomerKey,
      stripeSession: params.stripeSession,
      stripeIntentConfig: getIntentConfig({
        amount,
        stripeConfirmHandlerRef,
      }),
      stripeElementConfig: getElementConfig({
        returnURL: gravyOrderReturnURL,
        stripeCustomerId: params.stripeCustomerId,
        stripeCustomerKey: params.stripeCustomerKey,
      }),
      upsellItems: params.upsellItems,
      groupOrderLockReason: undefined,
    };
  },
);

const onBagReady: OrderingMachineAction = () => {
  orderingMachineLog.debug('Please provide `onBagReady` action.');
};

const onFetchPaymentBreakdownDone: OrderingMachineAction<FetchPaymentBreakdownResponse> = assign({
  amountEligibleForFlex(_, params) {
    return params.amountEligibleForFlex;
  },
});

const setUpsellBeingAdded: OrderingMachineAction = assign({
  lineItemIdsBeingUpdated({ context, event }) {
    assertEvent(event, 'ADD_UPSELL_TO_BAG');
    return [...context.lineItemIdsBeingUpdated, event.params.productId];
  },
});

const setLineItemIdBeingUpdated: OrderingMachineAction = assign({
  lineItemIdsBeingUpdated({ context, event }) {
    assertEvent(event, 'START_CHANGING_LINE_ITEM_QUANTITY');
    return [...context.lineItemIdsBeingUpdated, event.params.lineItemId];
  },
});

const removeLineItemIdBeingUpdated: OrderingMachineAction = assign({
  lineItemIdsBeingUpdated({ context, event }) {
    assertEvent(event, 'STOP_CHANGING_LINE_ITEM_QUANTITY');
    return context.lineItemIdsBeingUpdated.filter((id) => id !== event.params.lineItemId);
  },
});

const clearLineItemIdsBeingUpdated: OrderingMachineAction = assign({
  lineItemIdsBeingUpdated: [],
});

const onAddUpsellToBagDone: OrderingMachineAction<{ cart: OrderingCart | undefined }> = assign({
  lineItemIdsBeingUpdated: [],
  cart({ context }, params) {
    return params.cart ?? context.cart;
  },
});

const trackOrderPlaybackAutoSubmit: OrderingMachineAction = () => {
  orderingMachineLog.debug('Please provide `trackOrderPlaybackAutoSubmit` action.');
};

const trackOrderPlaybackGoBack: OrderingMachineAction = () => {
  orderingMachineLog.debug('Please provide `trackOrderPlaybackGoBack` action.');
};

const onUpdateLineItemDone: OrderingMachineAction = assign({
  selectedTip({ context, event }) {
    assertEvent(event, 'xstate.done.actor.updateLineItem');
    return getNewTip(
      context.selectedTip,
      context.cart.maxTipAmount,
      context.availableTips,
      event.output?.ledger,
    );
  },
  cart({ context, event }) {
    assertEvent(event, 'xstate.done.actor.updateLineItem');
    return event.output ?? context.cart;
  },
  appliedRewardId({ event }) {
    assertEvent(event, 'xstate.done.actor.updateLineItem');
    return event.output?.appliedRewardId;
  },
  lineItemIdsBeingUpdated: [],
});

const changeChannelOrLocation: OrderingMachineAction = () => {
  orderingMachineLog.debug('Please provide `changeChannelOrLocation` action.');
};

const requestLogin: OrderingMachineAction = () => {
  orderingMachineLog.debug('Please provide `requestLogin` action.');
};

const handleConvertToGroupOrderError: OrderingMachineAction<
  ConvertToGroupOrderErrorParams
> = () => {
  orderingMachineLog.debug('Please provide `handleConvertToGroupOrderError` action.');
};

const handleLeaveGroupOrderSuccess: OrderingMachineAction = () => {
  orderingMachineLog.debug('Please provide `handleLeaveGroupOrderSuccess` action.');
};

const handleLeaveGroupOrderError: OrderingMachineAction<LeaveGroupOrderErrorParams> = () => {
  orderingMachineLog.debug('Please provide `handleLeaveGroupOrderError` action.');
};

const setGroupOrderLockReason: OrderingMachineAction<GroupOrderLockReasonParams> = assign(
  (_args, params) => ({
    groupOrderLockReason: params.reason,
  }),
);

const clearGroupOrderLockReason: OrderingMachineAction = assign({
  groupOrderLockReason: undefined,
});

const onLockGroupOrderDone: OrderingMachineAction<GroupOrderCartParams> = assign({
  cart({ context }, params) {
    return { ...params.cart, wantedTimes: context.cart.wantedTimes };
  },
});

const handleLockGroupOrderError: OrderingMachineAction<LockGroupOrderErrorParams> = () => {
  orderingMachineLog.debug('Please provide `handleLockGroupOrderError` action.');
};

const unlockCartInBackground: OrderingMachineAction = () => {
  orderingMachineLog.debug('Please provide `unlockCartInBackground` action.');
};

const closeBag: OrderingMachineAction = () => {
  orderingMachineLog.debug('Please provide `closeBag` action.');
};

const onUnlockGroupOrderDone: OrderingMachineAction<GroupOrderCartParams> = assign({
  cart({ context }, params) {
    return { ...params.cart, wantedTimes: context.cart.wantedTimes };
  },
});

const handleUnlockGroupOrderError: OrderingMachineAction = () => {
  orderingMachineLog.debug('Please provide `handleUnlockGroupOrderError` action.');
};

const changeDeliveryPreferences: OrderingMachineAction = assign({
  cart({ context, event }) {
    assertEvent(event, 'CHANGE_DELIVERY_PREFERENCES');
    return { ...context.cart, deliveryPreferences: event.params };
  },
});

const changeTip: OrderingMachineAction = assign({
  selectedTip({ event }) {
    assertEvent(event, 'CHANGE_TIP');
    return event.params.tip;
  },
});

const changeDropoffLocation: OrderingMachineAction = assign({
  dropoffLocation({ event }) {
    assertEvent(event, 'CHANGE_DROPOFF_LOCATION');
    return event.params.dropoffLocation;
  },
});

const changeCreditUsage: OrderingMachineAction = assign({
  shouldUseCredit({ event }) {
    assertEvent(event, 'CHANGE_CREDIT_USAGE');
    return event.params.shouldUseCredit;
  },
});

const changeFlexUsage: OrderingMachineAction = assign({
  shouldUseFlex({ event }) {
    assertEvent(event, 'CHANGE_FLEX_USAGE');
    return event.params.shouldUseFlex;
  },
});

const changeUtensilsUsage: OrderingMachineAction = assign({
  shouldIncludeUtensils({ event }) {
    assertEvent(event, 'CHANGE_UTENSILS_USAGE');
    return event.params.shouldIncludeUtensils;
  },
});

const updateCartFromPolling: OrderingMachineAction<{ cart: OrderingCart }> = assign({
  cart({ context }, params) {
    return {
      ...context.cart,
      ...params.cart,
      ...(context?.cart?.wantedTimes ? { wantedTimes: context.cart.wantedTimes } : {}),
    };
  },
});

// ─────────────────────────────────────────────────────────────────────────────

export const OrderingActions = {
  resetContext,
  onFetchInitialDataDone,
  onBagReady,
  onFetchPaymentBreakdownDone,
  updateCartFromPolling,
  setUpsellBeingAdded,
  setLineItemIdBeingUpdated,
  removeLineItemIdBeingUpdated,
  clearLineItemIdsBeingUpdated,
  onAddUpsellToBagDone,
  trackOrderPlaybackAutoSubmit,
  trackOrderPlaybackGoBack,
  onUpdateLineItemDone,
  changeChannelOrLocation,
  requestLogin,
  handleConvertToGroupOrderError,
  handleLeaveGroupOrderSuccess,
  handleLeaveGroupOrderError,
  setGroupOrderLockReason,
  clearGroupOrderLockReason,
  onLockGroupOrderDone,
  handleLockGroupOrderError,
  unlockCartInBackground,
  closeBag,
  onUnlockGroupOrderDone,
  handleUnlockGroupOrderError,
  changeDeliveryPreferences,
  changeTip,
  changeDropoffLocation,
  changeCreditUsage,
  changeFlexUsage,
  changeUtensilsUsage,
  ...OrderingPaymentActions,
  ...OrderingPostOrderActions,
  ...OrderingRewardsActions,
  ...OrderingTimeActions,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type GroupOrderCartParams = {
  cart: OrderingCart;
};
